import {openDialog} from 'platform/components';

import i18n from '@dms/i18n';

import {AddParticipantDialog} from './AddParticipantDialog';
import {RecordAuditEmployeePickerDialog} from './RecordAuditEmployeePickerDialog';
import {useOptimisticAuditHeader} from './useOptimisticAuditHeader';

// xLarge is full-height, so the picker grid's flex/height chain resolves and the grid fills the
// dialog (a 'large' dialog is auto-height and would collapse the grid).
const PICKER_DIALOG_SIZE = 'xLarge';

/**
 * Dialog openers for the audit team (auditor / responsible person / participants), driven by the
 * optimistic header returned from useOptimisticAuditHeader. Shared by the detail page and the
 * embedded fill manager so titles, sizes, and confirm wiring never drift.
 */
export function useRecordAuditTeamPickers(header: ReturnType<typeof useOptimisticAuditHeader>) {
  const openAuditorPicker = () =>
    openDialog(<RecordAuditEmployeePickerDialog onConfirm={header.selectAuditor} />, {
      title: i18n.t('page.recordAudit.team.selectAuditor'),
      size: PICKER_DIALOG_SIZE,
    });

  const openResponsiblePersonPicker = () =>
    openDialog(
      <RecordAuditEmployeePickerDialog
        onConfirm={header.selectResponsiblePerson}
        onClear={header.clearResponsiblePerson}
      />,
      {
        title: i18n.t('page.recordAudit.team.selectResponsiblePerson'),
        size: PICKER_DIALOG_SIZE,
      }
    );

  const openAddParticipantDialog = () =>
    openDialog(
      <AddParticipantDialog
        existingEmployeeIds={header.effectiveParticipants.map(
          (participant) => participant.employeeId
        )}
        onConfirm={header.addParticipants}
      />,
      {
        title: i18n.t('page.recordAudit.create.actions.addParticipant'),
        size: PICKER_DIALOG_SIZE,
      }
    );

  return {openAuditorPicker, openResponsiblePersonPicker, openAddParticipantDialog};
}
